import axios from 'axios';
import api from './api';
import {
  billingRatesService,
  staffBaseRateService,
  flatRateService,
  BillingRate,
  FlatRate,
} from './newBillingService';

// Configuration
const API_BASE_URL = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:5003';

// ── Billing-Rate-driven pay calculation (mirrors backend StaffPayrollService) ──

function getDayType(date: Date): string {
  const d = date.getDay();
  if (d === 6) return 'Saturday';
  if (d === 0) return 'Sunday';
  return 'Weekday';
}

export interface ShiftSegment {
  name: string;
  hours: number;
  rate: number;
  amount: number;
  dayType: string;
  /** "Hourly" | "Sleepover" | "Travel" — drives unit label in the UI ($/hr vs flat vs $/km). */
  kind?: 'Hourly' | 'Sleepover' | 'Travel';
}

interface ShiftSegmentResult {
  segments: ShiftSegment[];
  totalAmount: number;
  totalHours: number;
}

/**
 * Slices a shift's start/end window into the matching StaffPay segments for
 * that day type and computes pay = baseRate × percentage for each slice.
 * This mirrors `StaffPayrollService.BuildShiftSegments` on the backend so the
 * UI preview matches what the backend will actually generate.
 */
function buildShiftSegments(
  start: Date,
  end: Date,
  baseRate: number,
  allSegments: BillingRate[]
): ShiftSegmentResult {
  const result: ShiftSegmentResult = { segments: [], totalAmount: 0, totalHours: 0 };
  if (end <= start) return result;
  const totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  result.totalHours = totalHours;
  if (baseRate <= 0 || allSegments.length === 0) return result;

  const dayType = getDayType(start);
  let daySegments = allSegments.filter(r => r.dayType === dayType);
  if (daySegments.length === 0) {
    daySegments = allSegments.filter(r => r.dayType === 'Weekday');
  }
  if (daySegments.length === 0) return result;

  // Non-Weekday day types typically have a single flat segment — apply across the whole shift.
  if (dayType !== 'Weekday' || daySegments.length === 1) {
    const seg = daySegments[0];
    const rate = Math.round(baseRate * seg.percentage / 100 * 100) / 100;
    const amount = Math.round(totalHours * rate * 100) / 100;
    result.segments.push({ name: seg.name, hours: totalHours, rate, amount, dayType, kind: 'Hourly' });
    result.totalAmount = amount;
    return result;
  }

  // Weekday: split the shift across the configured time windows.
  const shiftStartMin = start.getHours() * 60 + start.getMinutes();
  const shiftEndMinRaw = end.getHours() * 60 + end.getMinutes();
  const sameDay = start.toDateString() === end.toDateString();
  const overnight = !sameDay || shiftEndMinRaw <= shiftStartMin;
  const effectiveEnd = overnight ? shiftEndMinRaw + 1440 : shiftEndMinRaw;

  const sorted = [...daySegments].sort((a, b) => a.timeStartMinutes - b.timeStartMinutes);
  for (const seg of sorted) {
    let segStart = seg.timeStartMinutes;
    let segEnd = seg.timeEndMinutes;
    if (segEnd <= segStart) segEnd += 1440;
    if (overnight && segStart < 360) { segStart += 1440; segEnd += 1440; }

    const overlapStart = Math.max(shiftStartMin, segStart);
    const overlapEnd = Math.min(effectiveEnd, segEnd);
    if (overlapEnd <= overlapStart) continue;

    const hours = Math.round((overlapEnd - overlapStart) / 60 * 10000) / 10000;
    const rate = Math.round(baseRate * seg.percentage / 100 * 100) / 100;
    const amount = Math.round(hours * rate * 100) / 100;
    result.segments.push({ name: seg.name, hours, rate, amount, dayType, kind: 'Hourly' });
    result.totalAmount += amount;
  }
  result.totalAmount = Math.round(result.totalAmount * 100) / 100;
  return result;
}

// Enums — values must match the backend DTOs/StaffPayrollDtos.cs enum exactly
export enum PayrollStatus {
  Draft = 0,
  PendingStaffApproval = 1,
  StaffApproved = 2,
  PendingAdminApproval = 5,
  AdminApproved = 6,
  Rejected = 7,
  PendingPayment = 8,
  Paid = 9
}

export enum PayrollFrequency {
  Weekly = 1,
  BiWeekly = 2,
  Monthly = 3
}

export enum PaymentMethod {
  BankTransfer = 'BankTransfer',
  Check = 'Check',
  Cash = 'Cash',
  DirectDeposit = 'DirectDeposit'
}

// DTOs and Interfaces
/**
 * One slice of pay produced by the backend StaffPayrollService.
 * Each item represents a single Billing-Rate StaffPay segment (e.g. a Saturday
 * loading or a Weekday Evening loading) for one shift, so the user can see
 * exactly how the gross pay was assembled from Billing Rates.
 */
export interface PayrollItemDto {
  id: number;
  payrollId?: number;
  itemType: string;
  description: string;
  quantity: number;     // hours in this segment
  rate: number;         // effective $/hr after applying the segment % to base rate
  amount: number;       // quantity * rate
  isDeduction: boolean;
  shiftDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  billableHours?: number;
  hourlyRate?: number;
  staffName?: string;
  employeeId?: string;
}

export interface StaffPayrollDto {
  id: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  payrollStartDate: string;
  payrollEndDate: string;
  payrollFrequency: PayrollFrequency;
  payrollFrequencyText: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  /** Base hourly rate from Billing Rates → Staff Rates. Each PayrollItem may use a different effective rate. */
  hourlyRate: number;
  regularPay: number;
  overtimePay: number;
  grossPay: number;
  /** ATO PAYG withholding (NAT 1004 / NAT 3539, FY 2025-26). */
  taxDeductions: number;
  /** Reserved for non-PAYG deductions. With the new ATO calculator this is always 0. */
  otherDeductions: number;
  netPay: number;

  // ── Australian payroll: ATO calc surfaced from BuildPayrollFromShiftsAsync ──
  /** Employer SG contribution (12% of OTE, FY 2025-26) — paid on top of net, not deducted. */
  superContribution?: number;
  /** STP Phase 2 income-type code: SAW (default), WHM (working holiday maker), etc. */
  stpIncomeType?: string;
  /** Resolved ATO tax scale used for PAYG (e.g. "TaxFreeThreshold", "NoTfn", "WorkingHoliday"). */
  taxScale?: string;
  /** Compliance warnings raised by the casual payroll validator (e.g. SCHADS minimum). */
  complianceWarnings?: string[];
  /** TFN with all but the last 3 digits masked, for payslip display. */
  maskedTaxFileNumber?: string;
  abn?: string;
  bankAccountName?: string;
  bankBsb?: string;
  /** Bank account number with all but the last 3 digits masked. */
  maskedBankAccountNumber?: string;
  superFundName?: string;
  superFundUsi?: string;
  superFundAbn?: string;
  superMemberNumber?: string;

  status: PayrollStatus;
  statusText: string;
  generatedDate?: string;
  staffApprovalDate?: string;
  adminApprovalDate?: string;
  paymentDate?: string;
  staffApprovalNotes?: string;
  adminApprovalNotes?: string;
  paymentMethod?: string;
  transactionId?: string;
  paymentReference?: string;
  paymentNotes?: string;
  createdAt: string;
  updatedAt: string;
  includedShiftIds?: number[];
  /** Detailed Billing-Rate-based segment breakdown (one entry per shift × segment). */
  payrollItems?: PayrollItemDto[];
}

export interface PayrollFilters {
  staffId?: number;
  startDate?: string;
  endDate?: string;
  status?: PayrollStatus;
  payrollFrequency?: PayrollFrequency;
  page?: number;
  pageSize?: number;
}

export interface GeneratePayrollRequest {
  staffIds?: number[];
  shiftIds?: number[];    // Generate for specific shifts only
  startDate: string;
  endDate: string;
  payrollFrequency: PayrollFrequency;
  overtimeThreshold: number;
  overtimeMultiplier: number;
  includeOnlyCompletedShifts: boolean;
  /** Staff identity map keyed by staffId — avoids the backend needing to call the staff service */
  /**
   * Optional per-staff identity hints. Rates are NOT taken from here — the
   * backend always resolves pay from Billing Rates (StaffBaseRate × StaffPay
   * segment %). The legacy `hourlyRate` field is accepted for backward
   * compatibility with older callers but is ignored server-side.
   */
  staffDetails?: Record<number, { staffName: string; employeeId: string; hourlyRate?: number }>;
}

export interface WeeklyPayrollRequest {
  staffIds?: number[];
  weekStart: string;
  overtimeThreshold: number;
  overtimeMultiplier: number;
  includeOnlyCompletedShifts: boolean;
}

export interface AdminApprovalRequest {
  approvedBy: number;
  approvalNotes?: string;
}

export interface ApproveCompletedWorkRequest {
  approvalReason?: string;
  // Identity fields — the frontend sends these so the backend stores real data
  staffId?: number;
  staffName?: string;
  employeeId?: string;
  clientId?: number;
  clientName?: string;
  hourlyRate?: number;
  // Modified times for payroll calculation (optional)
  approvedStartTime?: string;
  approvedEndTime?: string;
  approvedTotalHours?: number;
  approvedRegularHours?: number;
  approvedOvertimeHours?: number;
  timeModificationReason?: string;
}

export interface PayrollRejectRequest {
  rejectedBy: number;
  rejectionReason: string;
  rejectionNotes?: string;
}

export interface PaymentProcessRequest {
  paymentMethod: PaymentMethod;
  transactionId: string;
  paymentReference?: string;
  paymentNotes?: string;
}

export interface CompletedWorkItem {
  id: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  clientId?: number;
  clientName: string;
  shiftDate: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string;
  actualEnd: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  /**
   * Base hourly rate sourced from Billing Rates → Staff Rates (StaffBaseRate).
   * NOT pulled from the staff profile. May be 0 if no active StaffBaseRate is
   * configured — see `rateSource` for the explanation in that case.
   */
  hourlyRate: number;
  /**
   * Total pay computed by slicing the shift into Billing-Rates StaffPay
   * segments (Weekday Daytime / Evening / Night, Saturday, Sunday, etc.) and
   * pricing each slice as `hourlyRate × segment.percentage`.
   */
  totalPay: number;
  /**
   * Human-readable explanation of where the rate came from (e.g.
   * "Billing Rates · 1 segment", or "No active StaffBaseRate configured").
   */
  rateSource?: string;
  /**
   * Per-segment breakdown of the shift's pay (one entry per StaffPay segment
   * the shift overlaps). Empty when no rate is configured.
   */
  segments?: ShiftSegment[];
  isCompleted: boolean;
  isApproved: boolean;
  completedDate: string;
  payrollGenerated?: boolean;
  payrollId?: number;
}

export interface PayrollSummary {
  totalPayrolls: number;
  pendingApprovals: number;
  readyForPayment: number;
  totalPaid: number;
  totalGrossPay: number;
  totalNetPay: number;
  thisMonth: {
    payrolls: number;
    grossPay: number;
    netPay: number;
  };
  thisWeek: {
    payrolls: number;
    grossPay: number;
    netPay: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

// Service implementation
class PayrollService {
  private readonly baseUrl = `${API_BASE_URL}/billing/payroll`;

  // Get payrolls with filtering
  async getPayrolls(filters?: PayrollFilters): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const params = new URLSearchParams();
      if (filters?.staffId) params.append('staffId', filters.staffId.toString());
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.status !== undefined) params.append('status', filters.status.toString());
      if (filters?.payrollFrequency !== undefined) params.append('payrollFrequency', filters.payrollFrequency.toString());
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

      const response = await axios.get(`${this.baseUrl}?${params}`);
      return { success: true, data: response.data.data || response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch payrolls' };
    }
  }

  // Get payroll by ID
  async getPayrollById(id: number): Promise<ApiResponse<StaffPayrollDto>> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}`);
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch payroll' };
    }
  }

  // Get payrolls for specific staff member
  async getStaffPayrolls(staffId: number, filters?: PayrollFilters): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.status !== undefined) params.append('status', filters.status.toString());
      if (filters?.payrollFrequency !== undefined) params.append('payrollFrequency', filters.payrollFrequency.toString());

      const response = await axios.get(`${this.baseUrl}/staff/${staffId}?${params}`);
      return { success: true, data: response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch staff payrolls' };
    }
  }

  // Generate payrolls
  async generatePayrolls(request: GeneratePayrollRequest): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const response = await axios.post(`${this.baseUrl}/generate`, request);
      return { success: true, data: response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to generate payrolls' };
    }
  }

  // Generate weekly payrolls
  async generateWeeklyPayrolls(request: WeeklyPayrollRequest): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const response = await axios.post(`${this.baseUrl}/generate-weekly`, request);
      return { success: true, data: response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to generate weekly payrolls' };
    }
  }

  // Staff approval (Draft → PendingAdminApproval)
  async staffApprovePayroll(payrollId: number, notes?: string): Promise<ApiResponse<StaffPayrollDto>> {
    try {
      const response = await axios.post(`${this.baseUrl}/${payrollId}/approve-staff`, { isApproved: true, notes });
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to submit payroll' };
    }
  }

  // Admin approval (PendingAdminApproval → AdminApproved)
  async adminApprovePayroll(payrollId: number, request: AdminApprovalRequest): Promise<ApiResponse<StaffPayrollDto>> {
    try {
      const response = await axios.post(`${this.baseUrl}/${payrollId}/approve-admin`, request);
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to approve payroll' };
    }
  }

  // Reject payroll
  async rejectPayroll(payrollId: number, request: PayrollRejectRequest): Promise<ApiResponse<StaffPayrollDto>> {
    try {
      const response = await axios.post(`${this.baseUrl}/${payrollId}/reject`, request);
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to reject payroll' };
    }
  }

  // Process payment
  async processPayment(payrollId: number, request: PaymentProcessRequest): Promise<ApiResponse<StaffPayrollDto>> {
    try {
      const response = await axios.post(`${this.baseUrl}/${payrollId}/process-payment`, request);
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to process payment' };
    }
  }

  // Get pending admin approvals
  async getPendingAdminApprovals(): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const response = await axios.get(`${this.baseUrl}/pending-admin-approvals`);
      return { success: true, data: response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch pending approvals' };
    }
  }

  // Get payrolls ready for payment
  async getPayrollsReadyForPayment(): Promise<ApiResponse<StaffPayrollDto[]>> {
    try {
      const response = await axios.get(`${this.baseUrl}/ready-for-payment`);
      return { success: true, data: response.data.data || response.data.Data || [] };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch payrolls ready for payment' };
    }
  }

  // Get payroll summary
  async getPayrollSummary(): Promise<ApiResponse<PayrollSummary>> {
    try {
      const response = await axios.get(`${this.baseUrl}/reports/summary`);
      return { success: true, data: response.data.data || response.data.Data };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to fetch payroll summary' };
    }
  }

  // Get completed work items ready for payroll generation
  async getCompletedWork(filters?: { 
    staffId?: number; 
    startDate?: string; 
    endDate?: string; 
    includeAlreadyProcessed?: boolean;
  }): Promise<ApiResponse<CompletedWorkItem[]>> {
    try {
      // Use the roster search endpoint to get completed shifts
      const params = new URLSearchParams();
      params.append('status', 'completed');
      params.append('pageSize', '100'); // Get more records
      
      if (filters?.staffId) params.append('staffId', filters.staffId.toString());
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      // Call the roster search API to get completed shifts
      const rosterUrl = `${API_BASE_URL}/roster/Shifts/search?${params}`;
      const response = await axios.get(rosterUrl);

      // Handle the response structure - check for Data property or direct array
      const shiftsData = response.data.data || response.data.Data || response.data.data || response.data;
      const shifts = Array.isArray(shiftsData) ? shiftsData : [];

      console.log('Fetched shifts:', shifts.length, 'completed shifts');

      // ── Load StaffPay billing-rate config ONCE per page load ────────────
      // The rate shown for each shift is computed from these segments and the
      // staff member's StaffBaseRate — NOT from `shift.hourlyRate` (which is
      // the legacy staff-profile rate and is intentionally ignored).
      let staffPaySegments: BillingRate[] = [];
      try {
        const ratesRes = await billingRatesService.getAllRates('StaffPay');
        if (ratesRes.success && ratesRes.data) {
          staffPaySegments = ratesRes.data.filter(r => r.isActive);
        }
      } catch (err) {
        console.warn('Failed to load StaffPay billing rates; pay totals will fall back to 0', err);
      }

      // ── Load StaffPay FlatRates (sleepover, travel) ────────────────────
      let sleepoverStaffRate: FlatRate | null = null;
      let travelStaffRate: FlatRate | null = null;
      try {
        const flatRes = await flatRateService.getAll(undefined, 'StaffPay');
        if (flatRes.success && flatRes.data) {
          const today = new Date();
          const isActive = (r: FlatRate) => r.isActive
            && new Date(r.effectiveFrom) <= today
            && (!r.effectiveTo || new Date(r.effectiveTo) >= today);
          sleepoverStaffRate = flatRes.data.find(r => r.category === 'Sleepover' && isActive(r)) ?? null;
          travelStaffRate = flatRes.data.find(r => r.category === 'Transport' && isActive(r)) ?? null;
        }
      } catch (err) {
        console.warn('Failed to load StaffPay FlatRates; sleepover/travel will not appear in pay preview', err);
      }

      // ── Pre-fetch every unique staff profile in parallel (one round-trip) ─
      const uniqueStaffIds = Array.from(new Set(
        shifts.map((s: any) => s.staffId).filter((id: any) => !!id)
      )) as number[];

      type StaffProfileLite = { firstName?: string; lastName?: string; name?: string; employeeId?: string };
      const staffProfiles = new Map<number, StaffProfileLite>();
      await Promise.all(uniqueStaffIds.map(async (staffId) => {
        try {
          const r = await axios.get(`${API_BASE_URL}/staff/internal/${staffId}`);
          if (r.data?.success && r.data.data) {
            staffProfiles.set(staffId, r.data.data);
          }
        } catch {
          // staff lookup failed — name will fall back to "Staff #<id>"
        }
      }));

      // ── Resolve the active StaffBaseRate for each unique staff member ────
      // The rate is keyed in the database by `staffId`. Most callers store the
      // internal staff PK there, but historically some entries may have been
      // keyed by the numeric portion of the employeeId (e.g. user typed
      // "20250001" because that's what they saw in the UI). To stay robust to
      // either representation, we try both keys before giving up.
      const staffBaseRates = new Map<number, number>();
      const staffRateKeyUsed = new Map<number, 'staffId' | 'employeeId'>();
      await Promise.all(uniqueStaffIds.map(async (staffId) => {
        const tryFetch = async (key: number): Promise<number | null> => {
          try {
            const res = await staffBaseRateService.getActiveForStaff(key);
            if (res?.success && res.data?.baseHourlyRate != null) {
              return Number(res.data.baseHourlyRate);
            }
          } catch {
            // no rate at this key — fall through
          }
          return null;
        };

        // Try the internal PK first
        let rate = await tryFetch(staffId);
        if (rate != null) {
          staffBaseRates.set(staffId, rate);
          staffRateKeyUsed.set(staffId, 'staffId');
          return;
        }
        // Fall back to the numeric portion of employeeId (e.g. EMP20250001 → 20250001)
        const profile = staffProfiles.get(staffId);
        const empNumeric = profile?.employeeId
          ? parseInt(String(profile.employeeId).replace(/\D/g, ''), 10)
          : NaN;
        if (Number.isFinite(empNumeric) && empNumeric > 0 && empNumeric !== staffId) {
          rate = await tryFetch(empNumeric);
          if (rate != null) {
            staffBaseRates.set(staffId, rate);
            staffRateKeyUsed.set(staffId, 'employeeId');
            return;
          }
        }
      }));

      // Transform shift data to completed work items
      let completedWork: CompletedWorkItem[] = [];

      for (const shift of shifts) {
        const startDateTime = new Date(shift.startDateTime || shift.startDate);
        const endDateTime = new Date(shift.endDateTime || shift.endDate);
        const hasActualStart = !!shift.actualStartTime;
        const hasActualEnd   = !!shift.actualEndTime;
        const actualStartDt  = hasActualStart ? new Date(shift.actualStartTime) : startDateTime;
        const actualEndDt    = hasActualEnd   ? new Date(shift.actualEndTime)   : endDateTime;

        const totalHours = Math.max(0, (actualEndDt.getTime() - actualStartDt.getTime()) / (1000 * 60 * 60));

        // ── Rate + total pay come from Billing Rates only ───────────────────
        const baseRate = staffBaseRates.get(shift.staffId) ?? 0;
        const isSleepover = !!shift.isSleepover;
        const travelKm = typeof shift.travelKilometres === 'number' ? shift.travelKilometres : 0;
        let totalPay = 0;
        let rateSource: string;
        let shiftSegments: ShiftSegment[] = [];

        if (isSleepover) {
          if (sleepoverStaffRate) {
            const amount = Math.round(sleepoverStaffRate.unitRate * 100) / 100;
            shiftSegments.push({
              name: 'Sleepover (Inactive Overnight)',
              hours: 1,
              rate: sleepoverStaffRate.unitRate,
              amount,
              dayType: 'Sleepover',
              kind: 'Sleepover',
            });
            totalPay = amount;
            rateSource = `Sleepover flat rate · $${sleepoverStaffRate.unitRate.toFixed(2)}/night`;
          } else {
            rateSource = 'Shift is sleepover but no active Sleepover StaffPay FlatRate is configured (Billing Rates → Special Rates)';
          }
        } else if (baseRate <= 0) {
          rateSource = 'No active StaffBaseRate configured for this staff member (set one under Billing Rates → Staff Rates using their internal Staff ID)';
        } else if (staffPaySegments.length === 0) {
          rateSource = 'No active StaffPay segments configured (set them under Billing Rates → Rate Structure)';
        } else {
          const calc = buildShiftSegments(actualStartDt, actualEndDt, baseRate, staffPaySegments);
          totalPay = calc.totalAmount;
          shiftSegments = calc.segments;
          const matchedBy = staffRateKeyUsed.get(shift.staffId);
          const matchNote = matchedBy === 'employeeId'
            ? ' · matched by employeeId fallback'
            : '';
          rateSource = `Billing Rates · ${calc.segments.length} segment${calc.segments.length === 1 ? '' : 's'}${matchNote}`;
        }

        if (travelKm > 0 && travelStaffRate) {
          const km = Math.round(travelKm * 100) / 100;
          const amount = Math.round(km * travelStaffRate.unitRate * 100) / 100;
          shiftSegments.push({
            name: `Travel (${km.toFixed(2)} km)`,
            hours: km,
            rate: travelStaffRate.unitRate,
            amount,
            dayType: 'Travel',
            kind: 'Travel',
          });
          totalPay = Math.round((totalPay + amount) * 100) / 100;
          rateSource = `${rateSource} + travel reimbursement`;
        } else if (travelKm > 0 && !travelStaffRate) {
          rateSource = `${rateSource} (travel km recorded but no Transport StaffPay FlatRate configured)`;
        }

        const profile = staffProfiles.get(shift.staffId);
        let staffName = 'Unknown Staff';
        let clientName = 'Unknown Client';
        let employeeId = `EMP${shift.staffId || 0}`;
        if (profile) {
          staffName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
            || profile.name
            || `Staff ${shift.staffId}`;
          employeeId = profile.employeeId || `EMP${shift.staffId}`;
        }

        try {
          if (shift.clientId) {
            const clientResponse = await axios.get(`${API_BASE_URL}/clients/internal/${shift.clientId}`);
            if (clientResponse.data?.success && clientResponse.data.data) {
              const clientData = clientResponse.data.data;
              clientName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || clientData.name || `Client ${shift.clientId}`;
            }
          }
        } catch (error) {
          console.warn(`Failed to fetch client ${shift.clientId}:`, error);
        }

        const workItem: CompletedWorkItem = {
          id: shift.id || Math.random() * 1000,
          staffId: shift.staffId || 0,
          staffName,
          employeeId,
          clientId: shift.clientId,
          clientName,
          shiftDate: startDateTime.toISOString().split('T')[0],
          scheduledStart: startDateTime.toTimeString().substring(0, 5),
          scheduledEnd: endDateTime.toTimeString().substring(0, 5),
          actualStart: actualStartDt.toTimeString().substring(0, 5),
          actualEnd: actualEndDt.toTimeString().substring(0, 5),
          totalHours: Math.round(totalHours * 100) / 100,
          // The legacy regular/overtime split is no longer meaningful — pay is
          // segmented by Billing Rates, not a 40h/week threshold. Keep these
          // populated for back-compat (UI tooltips, table summary) but treat
          // all hours as regular.
          regularHours: Math.round(totalHours * 100) / 100,
          overtimeHours: 0,
          hourlyRate: baseRate,
          totalPay,
          rateSource,
          segments: shiftSegments,
          isCompleted: shift.status === 'completed',
          isApproved: false, // Will be checked via API call
          completedDate: shift.actualEndTime || shift.endDateTime || shift.endDate,
        };

        completedWork.push(workItem);
      }

      console.log('Transformed completed work items:', completedWork.length);
      
      // Fetch approval statuses from backend
      try {
        const approvalsResponse = await fetch(`${API_BASE_URL}/billing/payroll/completed-work-approvals`);
        if (approvalsResponse.ok) {
          const approvalsData = await approvalsResponse.json();
          if (approvalsData.success && approvalsData.data) {
            // Create a map of shift approvals
            const approvalMap = new Map();
            approvalsData.data.forEach((approval: any) => {
              approvalMap.set(approval.shiftId, approval);
            });
            
            // Update completed work with approval status and filter out rejected
            completedWork = completedWork
              .map(work => {
                const approval = approvalMap.get(work.id);
                if (approval) {
                  work.isApproved = approval.isApproved;
                }
                return work;
              })
              .filter(work => {
                const approval = approvalMap.get(work.id);
                return !approval || !approval.isRejected; // Include if no approval record or not rejected
              });
          }
        }
      } catch (error) {
        console.warn('Could not fetch approval statuses:', error);
      }
      
      return { success: true, data: completedWork };
    } catch (error: any) {
      console.error('Error fetching completed work:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message || 'Failed to fetch completed work',
        data: [] // Return empty array as fallback
      };
    }
  }

  // Delete payroll
  async deletePayroll(payrollId: number): Promise<ApiResponse<void>> {
    try {
      await axios.delete(`${this.baseUrl}/${payrollId}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete payroll' };
    }
  }

  async downloadPdf(payrollId: number, staffName?: string, startDate?: string): Promise<void> {
    const response = await axios.get(`${this.baseUrl}/${payrollId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const name = staffName ? staffName.replace(/\s+/g, '-') : String(payrollId);
    const date = startDate ? startDate.split('T')[0] : '';
    link.download = `payslip-${name}${date ? '-' + date : ''}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async sendPayrollEmail(payrollId: number, recipientEmail: string): Promise<ApiResponse<void>> {
    try {
      const response = await axios.post(`${this.baseUrl}/${payrollId}/send-email`, { recipientEmail });
      return { success: true, message: response.data?.message };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Failed to send email' };
    }
  }

  // Utility methods
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getStatusColor(status: PayrollStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case PayrollStatus.Draft:
        return 'default';
      case PayrollStatus.PendingStaffApproval:
        return 'warning';
      case PayrollStatus.StaffApproved:
        return 'info';
      case PayrollStatus.PendingAdminApproval:
        return 'warning';
      case PayrollStatus.AdminApproved:
        return 'success';
      case PayrollStatus.Rejected:
        return 'error';
      case PayrollStatus.PendingPayment:
        return 'primary';
      case PayrollStatus.Paid:
        return 'success';
      default:
        return 'default';
    }
  }

  getPayrollFrequencyText(frequency: PayrollFrequency): string {
    switch (frequency) {
      case PayrollFrequency.Weekly:
        return 'Weekly';
      case PayrollFrequency.BiWeekly:
        return 'Bi-Weekly';
      case PayrollFrequency.Monthly:
        return 'Monthly';
      default:
        return 'Unknown';
    }
  }

  getStatusText(status: PayrollStatus): string {
    switch (status) {
      case PayrollStatus.Draft:
        return 'Draft';
      case PayrollStatus.PendingStaffApproval:
        return 'Pending Staff Approval';
      case PayrollStatus.StaffApproved:
        return 'Staff Approved';
      case PayrollStatus.PendingAdminApproval:
        return 'Pending Admin Approval';
      case PayrollStatus.AdminApproved:
        return 'Admin Approved';
      case PayrollStatus.Rejected:
        return 'Rejected';
      case PayrollStatus.PendingPayment:
        return 'Pending Payment';
      case PayrollStatus.Paid:
        return 'Paid';
      default:
        return 'Unknown';
    }
  }

  async approveCompletedWork(workId: number, request?: ApproveCompletedWorkRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await fetch(`${API_BASE_URL}/billing/payroll/completed-work/${workId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: request ? JSON.stringify(request) : undefined,
      });

      if (!response.ok) {
        throw new Error(`Failed to approve work: ${response.statusText}`);
      }

      const data = await response.json();
      return { success: true, data: true, message: 'Work approved successfully' };
    } catch (error: any) {
      console.error('Error approving completed work:', error);
      return { 
        success: false, 
        errors: [error.message || 'Failed to approve completed work']
      };
    }
  }

  async rejectCompletedWork(workId: number, reason?: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await fetch(`${API_BASE_URL}/billing/payroll/completed-work/${workId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: reason || 'Work rejected' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to reject work: ${response.statusText}`);
      }

      const data = await response.json();
      return { success: true, data: true, message: 'Work rejected successfully' };
    } catch (error: any) {
      console.error('Error rejecting completed work:', error);
      return { 
        success: false, 
        errors: [error.message || 'Failed to reject completed work']
      };
    }
  }

  async updateShift(shiftId: number, updatedData: {
    actualStartTime: string;
    actualEndTime: string;
    hourlyRate: number;
    notes: string;
    totalHours: number;
  }, shiftDate?: string): Promise<ApiResponse<CompletedWorkItem>> {
    try {
      // Use the shift's own date; fall back to today only as a last resort
      const dateToUse = shiftDate || new Date().toISOString().split('T')[0];
      
      const updatePayload = {
        actualStartTime: `${dateToUse}T${updatedData.actualStartTime}:00`,
        actualEndTime: `${dateToUse}T${updatedData.actualEndTime}:00`,
        hourlyRate: updatedData.hourlyRate,
        notes: updatedData.notes,
        totalHours: updatedData.totalHours
      };

      const response = await fetch(`${API_BASE_URL}/billing/payroll/shifts/${shiftId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update shift: ${response.statusText}`);
      }

      const data = await response.json();
      return { 
        success: true, 
        data: data.data, 
        message: 'Shift updated successfully' 
      };
    } catch (error: any) {
      console.error('Error updating shift:', error);
      return { 
        success: false, 
        errors: [error.message || 'Failed to update shift']
      };
    }
  }

  async getIncludedShiftIds(): Promise<ApiResponse<number[]>> {
    try {
      const response = await axios.get(`${this.baseUrl}/included-shift-ids`);
      return { success: true, data: response.data.data || [] };
    } catch (error: any) {
      return { success: false, data: [], message: error.response?.data?.message || 'Failed to fetch included shift IDs' };
    }
  }
}

export const payrollService = new PayrollService();
